import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

export const OwnersPage = () => {
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const ownersSnap = await getDocs(collection(db, 'owners'));
        const ownersData = ownersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOwners(ownersData);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load owners');
      } finally {
        setLoading(false);
      }
    };
    fetchOwners();
  }, []);

  const fetchOwnerDetails = async (ownerId) => {
    setSelectedOwner(ownerId);
    setLoading(true);
    try {
      // Fetch vehicles for this owner
      const vehiclesSnap = await getDocs(query(collection(db, 'vehicles'), where('ownerId', '==', ownerId)));
      const vehiclesData = vehiclesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVehicles(vehiclesData);

      // Fetch drivers for this owner
      const driversSnap = await getDocs(query(collection(db, 'drivers'), where('ownerId', '==', ownerId)));
      const driversData = driversSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDrivers(driversData);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load owner details');
    } finally {
      setLoading(false);
    }
  };

  const toggleVehicleStatus = async (vehicleId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'vehicles', vehicleId), { isActive: !currentStatus });
      toast.success(`Vehicle ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchOwnerDetails(selectedOwner); // refresh
    } catch (err) {
      toast.error('Failed to update vehicle status');
    }
  };

  const toggleDriverKyc = async (driverId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'drivers', driverId), { kycVerified: !currentStatus });
      toast.success(`KYC ${!currentStatus ? 'approved' : 'revoked'}`);
      fetchOwnerDetails(selectedOwner);
    } catch (err) {
      toast.error('Failed to update KYC status');
    }
  };

  if (loading && owners.length === 0) return <div className="flex justify-center p-8"><div className="text-gray-400">Loading fleet owners...</div></div>;

  return (
    <div className="space-y-6">
      {/* Owner List */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Fleet Owners</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-2 text-left">Business Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Phone</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {owners.map(owner => (
                <tr key={owner.id} className="border-t border-gray-700">
                  <td className="px-4 py-2">{owner.businessName || owner.name || '-'}</td>
                  <td className="px-4 py-2">{owner.email || '-'}</td>
                  <td className="px-4 py-2">{owner.phone || '-'}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => fetchOwnerDetails(owner.id)}
                      className="bg-emerald-600 hover:bg-emerald-500 px-3 py-1 rounded text-xs"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Owner Details (if selected) */}
      {selectedOwner && (
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Vehicles</h3>
            {vehicles.length === 0 ? <p className="text-gray-400">No vehicles registered.</p> : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-700">
                  <tr><th className="px-4 py-2">Registration</th><th className="px-4 py-2">Model</th><th className="px-4 py-2">Assigned Driver</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Action</th></tr></thead>
                <tbody>
                  {vehicles.map(vehicle => (
                    <tr key={vehicle.id} className="border-t border-gray-700">
                      <td className="px-4 py-2">{vehicle.plateNumber || vehicle.registration || '-'}</td>
                      <td className="px-4 py-2">{vehicle.model || vehicle.make || '-'}</td>
                      <td className="px-4 py-2">{vehicle.assignedDriverId?.slice(-6) || 'Unassigned'}</td>
                      <td className="px-4 py-2">{vehicle.isActive ? '✅ Active' : '❌ Inactive'}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => toggleVehicleStatus(vehicle.id, vehicle.isActive)} className="text-xs bg-gray-600 px-2 py-1 rounded">
                          {vehicle.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Drivers</h3>
            {drivers.length === 0 ? <p className="text-gray-400">No drivers assigned.</p> : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-700">
                  <tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">KYC</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Action</th></tr></thead>
                <tbody>
                  {drivers.map(driver => (
                    <tr key={driver.id} className="border-t border-gray-700">
                      <td className="px-4 py-2">{driver.businessName || driver.fullName || '-'}</td>
                      <td className="px-4 py-2">{driver.email || '-'}</td>
                      <td className="px-4 py-2">{driver.kycVerified ? '✅ Verified' : '❌ Pending'}</td>
                      <td className="px-4 py-2">{driver.online ? 'Online' : 'Offline'}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => toggleDriverKyc(driver.id, driver.kycVerified)} className="text-xs bg-blue-600 px-2 py-1 rounded">
                          {driver.kycVerified ? 'Revoke KYC' : 'Approve KYC'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <button
            onClick={() => setSelectedOwner(null)}
            className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-sm"
          >
            Close Details
          </button>
        </div>
      )}
    </div>
  );
};
